import { FC, useState } from 'react';
import { CareMapNotesList } from './note-list/CareMapNotesList';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ICareMapNoteTarget } from './model/ICareMapNoteTarget';
import { CareMapState } from '../caremap/CareMapState';
import { getNoteTrackingContext } from './generic-notes/NotesTrackingHelper';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';
import { useSubscription } from '@meraki-internal/state';
import { AddNoteButton } from './add-note/AddNoteButton';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { NotesSortControl, SortField, SortOrder } from './components/NotesSortControl';
import { NotesFilterControl, FilterState } from './components/NotesFilterControl';

export type INotesViewProps = {
    noteTarget: ICareMapNoteTarget;
    titlePrefix?: string;
};

export const CareMapNotesView: FC<INotesViewProps> = ({ noteTarget, titlePrefix = '' }) => {

    if (noteTarget.memberId === ''){
        noteTarget.memberId = undefined;
    }

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);
    const history = useInstance(HistoryViewModel);
    const cmState = useInstance(CareMapState);
    const tracking = useInstance(MixpanelService);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const [startTimestamp, setStartTimestamp] = useState<number>(Date.now());
    const [sortField, setSortField] = useState<SortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
    const [filters, setFilters] = useState<FilterState>({
        noteType: [],
        careCategory: [],
        tags: []
    });

    const caremap = cmState.getActiveCaremap();

    // Guard against accessing caremap before it's loaded
    if (!caremap) {
        return null;
    }

    const section = caremap[noteTarget.section];

    const editNote = (noteId: string) => {
        history.push(`${history.getCurrentLocation().pathname}/${noteId}`);
    };

    const addNote = () => {
        history.push(`${history.getCurrentLocation().pathname}/add-note`);
    };

    let node = section;
    if (noteTarget.memberId) {
        node = (section as any).members.find((m: any) => m.id === noteTarget.memberId);
    }

    const onDidEnter = () => {
        setStartTimestamp(Date.now());
        drawerState.setIsMaskingFocusedCareMap(true);
    };

    const onWillLeave = () => {
        const timeOnPage = (Date.now() - startTimestamp) / 1000;
        const {location, type} = getNoteTrackingContext({noteTarget, node, note: undefined});
        tracking.trackWithDebounce({
            event: 'Notes List Viewed',
            getContext: () => ({location, type, timeOnPage})
        });
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const canEdit = caremap.links.self.actions.includes('edit-notes');

    const allNotes = notesFactory.forActiveCaremap().getNotesForTarget(noteTarget);

    // Filter notes based on active filters
    const filterNotes = (notesToFilter: any[]) => {
        return notesToFilter.filter(note => {
            // Note Type filters (AND logic - note must have ALL selected types)
            if (filters.noteType.length > 0) {
                const hasRecording = Boolean(note.otherAttributes?.recording);
                const hasActionItems = Boolean(note.otherAttributes?.recording?.summary?.actionItems?.length > 0);
                const hasOtherAttachments = Boolean(note.attachments?.length > 0) && (!hasRecording || note.attachments?.length > 1);
                const matchesAllNoteTypes = filters.noteType.every(filterType => {
                    switch (filterType) {
                        case 'hasRecording': return hasRecording;
                        case 'hasActionItems': return hasActionItems;
                        case 'hasOtherAttachments': return hasOtherAttachments;
                        default: return false;
                    }
                });
                if (!matchesAllNoteTypes) return false;
            }
            // Care Category filters
            if (filters.careCategory.length > 0) {
                const noteSection = note.otherAttributes?.noteTarget?.section;
                if (!filters.careCategory.includes(noteSection)) return false;
            }

            // Tags filters (OR logic - note must have at least one selected tag)
            if (filters.tags.length > 0) {
                const noteTags = note.tags || [];
                const hasMatchingTag = filters.tags.some(filterTag => noteTags.includes(filterTag));
                if (!hasMatchingTag) return false;
            }

            return true;
        });
    };
    const notes = filterNotes(allNotes);

    // Check if any filters are active (only noteType and tags in caremap context)
    const hasActiveFilters = filters.noteType.length > 0 || filters.tags.length > 0;

    return (
        <CareMapDrawerPage
            hasBack={true}
            title={`${titlePrefix?.concat(' ')}Notes`}
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            headerButtons={
                <>
                    {allNotes.length > 0 && (
                        <>
                            <NotesSortControl
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSortFieldChange={setSortField}
                                onSortOrderChange={setSortOrder}
                                triggerId="sort-trigger-caremap-drawer"
                            />
                            <NotesFilterControl
                                filters={filters}
                                onFiltersChange={setFilters}
                                notes={allNotes}
                                triggerId="filter-trigger-caremap-drawer"
                                hideCareCategory={true}
                            />
                        </>
                    )}
                    <AddNoteButton
                        useIcon
                        canAdd={canEdit}
                        onClick={addNote}
                    />
                </>
            }
        >
            <CareMapNotesList
                notes={notes}
                showHeading={false}
                onNoteClicked={editNote}
                onAddNote={addNote}
                data-type="more-notes-list"
                showSortOptions={false}
                sortField={sortField}
                sortOrder={sortOrder}
                hasActiveFilters={hasActiveFilters}
            />
        </CareMapDrawerPage>
    );
};
